import type { HeaderSelector } from '@tempo-io/swimlane-transforms';
import { z } from 'zod';
import { type SwimlaneLayoutDTO, swimlaneLayoutDTOSchema } from './layout-dto';

/**
 * Header type for column and row headers in published swimlanes.
 * Matches the frontend's IHeader interface from ui/swimlane/types.
 */
export interface IHeader {
  id: string;
  label: string;
  selector: HeaderSelector;
  level: 1 | 2 | 3;
  headerIndex: number;
  isDateHeader: boolean;
  headerColor?: string;
  startDate?: string;
  endDate?: string;
}

/**
 * Sub-item reference in published swimlane items
 */
export interface SubItemReference {
  id: string;
  title: string;
  itemTypeId: string;
  itemTypeName: string;
}

/**
 * Enriched item type for published swimlanes
 * Contains all item data plus enriched team and status information
 */
export interface IdentifiableTItem {
  id: number;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  progress: number | null;
  isDeletable: boolean;
  attachments: number;
  borderColor: string;
  comments: number;
  keyDates: number;
  linkedItems: number;
  labels: string[];
  subItems: SubItemReference[];

  // Item type information
  type: string; // Item type name (e.g., "Epic", "Story", "Task")
  itemTypeId: number;

  // Optional parent reference
  parentId?: string;
  parentTitle?: string;

  // Team information
  teamId?: number;
  teamName?: string;
  teamColor?: string;

  // Status information
  itemStatusId?: number;
  statusName?: string;
  statusType?: string;
}

/**
 * Item dependency for published swimlanes
 */
export interface ItemDependency {
  dependencyType: 'PREDECESSOR' | 'SUCCESSOR';
  dependencyItemId: number;
}

/**
 * Detailed item information from ItemClient
 * Contains all fields from work items for panel details
 */
export interface ItemDetails {
  id: number;
  name: string;
  description: string | null;
  itemTypeId: number;
  statusId: number | undefined;
  parentId: number | null;
  teamId: number | null;
  targetStartDateTime: string | null;
  targetFinishDateTime: string | null;
  customFields: Record<string, unknown>;
  timeSpentSeconds: number | null;
  estimationId: number | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  doneDateTime: string | null;
  externalKey: string | null;
  externalLink: string | null;
  planId: number | null;
  dependencies: ItemDependency[];
}

/**
 * Legend item representing a color-to-category mapping for the swimlane legend.
 */
export interface LegendItem {
  color: string;
  label: string;
}

/**
 * Publishing preferences for controlling what elements are displayed in published views.
 * Derived from LayoutOptionsDTO with semantic naming for frontend consumption.
 */
export interface PublishingPreferences {
  showTitle: boolean;
  showVisionStatement: boolean;
  showPublishDate: boolean;
  showLegend: boolean;
  showTempoBranding: boolean;
}

/**
 * Metadata for published swimlane views
 */
export interface PublishedMetadata {
  title: string;
  viewId: number;
  createdAt: string;
  publishedAt: string;
  visionStatement?: string;
  preferences?: PublishingPreferences;
}

/**
 * Response type for published swimlane endpoint
 * Contains all data needed to render a published swimlane view
 */
export interface PublishedSwimlaneResponse {
  items: Record<string, IdentifiableTItem>;
  columnHeaders: IHeader[];
  rowHeaders: IHeader[];
  cells: Record<string, Record<string, string[]>>;
  metadata: PublishedMetadata;
  layout?: SwimlaneLayoutDTO;
  itemDetails: Record<string, ItemDetails>;
  columnAxisLabel: string;
  rowAxisLabel: string;
  legendItems?: LegendItem[];
}

// =============================================================================
// Zod Schemas for runtime validation
// =============================================================================

/**
 * HeaderSelector schemas - discriminated union matching @tempo-io/swimlane-transforms
 */
const customPropertySelectorSchema = z.object({
  type: z.literal('CustomPropertySelectorByID'),
  customPropertyID: z.string(),
});

const parentItemSelectorSchema = z.object({
  type: z.literal('ParentItemSelector'),
});

const dateHeaderSelectorSchema = z.object({
  type: z.literal('DateSelector'),
  header: z.string(),
});

export const headerSelectorSchema = z.discriminatedUnion('type', [
  customPropertySelectorSchema,
  parentItemSelectorSchema,
  dateHeaderSelectorSchema,
]);

/**
 * SubItemReference schema
 */
export const subItemReferenceSchema = z.object({
  id: z.string(),
  title: z.string(),
  itemTypeId: z.string(),
  itemTypeName: z.string(),
});

/**
 * IHeader schema - matches frontend IHeader interface
 */
export const iHeaderSchema = z.object({
  id: z.string(),
  label: z.string(),
  selector: headerSelectorSchema,
  level: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  headerIndex: z.number().int(),
  isDateHeader: z.boolean(),
  headerColor: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
});

/**
 * IdentifiableTItem schema - enriched item with team/status information
 */
export const identifiableTItemSchema = z.object({
  id: z.number().int(),
  title: z.string(),
  description: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  progress: z.number().nullable(),
  isDeletable: z.boolean(),
  attachments: z.number().int(),
  borderColor: z.string(),
  comments: z.number().int(),
  keyDates: z.number().int(),
  linkedItems: z.number().int(),
  labels: z.array(z.string()),
  subItems: z.array(subItemReferenceSchema),
  type: z.string(),
  itemTypeId: z.number().int(),
  parentId: z.string().optional(),
  parentTitle: z.string().optional(),
  teamId: z.number().int().optional(),
  teamName: z.string().optional(),
  teamColor: z.string().optional(),
  itemStatusId: z.number().int().optional(),
  statusName: z.string().optional(),
  statusType: z.string().optional(),
});

/**
 * ItemDependency schema
 */
const itemDependencySchema = z.object({
  dependencyType: z.enum(['PREDECESSOR', 'SUCCESSOR']),
  dependencyItemId: z.number().int(),
});

/**
 * ItemDetails schema - detailed item information from ItemClient
 */
export const itemDetailsSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  description: z.string().nullable(),
  itemTypeId: z.number().int(),
  statusId: z.union([z.number().int(), z.undefined()]), // Required field that can be undefined
  parentId: z.number().int().nullable(),
  teamId: z.number().int().nullable(),
  targetStartDateTime: z.string().nullable(),
  targetFinishDateTime: z.string().nullable(),
  customFields: z.record(z.string(), z.unknown()),
  timeSpentSeconds: z.number().nullable(),
  estimationId: z.number().int().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.string(),
  updatedBy: z.string(),
  doneDateTime: z.string().nullable(),
  externalKey: z.string().nullable(),
  externalLink: z.string().nullable(),
  planId: z.number().int().nullable(),
  dependencies: z.array(itemDependencySchema),
});

/**
 * LegendItem schema
 */
export const legendItemSchema = z.object({
  color: z.string(),
  label: z.string(),
});

/**
 * PublishingPreferences schema
 */
export const publishingPreferencesSchema = z.object({
  showTitle: z.boolean(),
  showVisionStatement: z.boolean(),
  showPublishDate: z.boolean(),
  showLegend: z.boolean(),
  showTempoBranding: z.boolean(),
});

/**
 * PublishedMetadata schema
 */
export const publishedMetadataSchema = z.object({
  title: z.string(),
  viewId: z.number().int(),
  createdAt: z.string(),
  publishedAt: z.string(),
  visionStatement: z.string().optional(),
  preferences: publishingPreferencesSchema.optional(),
});

/**
 * PublishedSwimlaneResponse schema - main response type for published swimlane endpoint
 */
export const publishedSwimlaneResponseSchema = z.object({
  items: z.record(z.string(), identifiableTItemSchema),
  columnHeaders: z.array(iHeaderSchema),
  rowHeaders: z.array(iHeaderSchema),
  cells: z.record(z.string(), z.record(z.string(), z.array(z.string()))),
  metadata: publishedMetadataSchema,
  layout: swimlaneLayoutDTOSchema.optional(),
  itemDetails: z.record(z.string(), itemDetailsSchema),
  columnAxisLabel: z.string(),
  rowAxisLabel: z.string(),
  legendItems: z.array(legendItemSchema).optional(),
});

// Type alignment check - verify schema output is assignable to interface
// Note: We check one direction only because Zod produces mutable types while
// the interface uses `readonly` for HeaderSelector (from swimlane-transforms).
// At runtime, the values are identical - readonly is a compile-time constraint only.
type SchemaType = z.infer<typeof publishedSwimlaneResponseSchema>;
type _AssignableToInterface = SchemaType extends PublishedSwimlaneResponse ? true : never;
const _typeCheck: _AssignableToInterface = true;
void _typeCheck;
